import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip,
  Stack, Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';

const CONTENT = {
  English: {
    title: 'Help & FAQ', subtitle: 'Quick answers when something is not working as expected.',
    guide: 'Open User Guide', contact: 'Contact support', note: 'For step-by-step feature instructions, use the User Guide.',
    groups: [
      ['Getting unstuck', [
        ['Why is a button unavailable?', 'Check the message near the button. A required field, photo, paid-plan check, or an unfinished generation may be blocking the next step.'],
        ['Why did generation fail?', 'Wait a moment and try once more. Check your connection and uploaded file. If it keeps failing, note the activity and time, then contact support.'],
        ['Why is there no sound?', 'Turn on Buddy voice in the activity, raise the device volume, and allow browser audio. Some browsers require you to click the page before sound can play.'],
      ]],
      ['Saving & devices', [
        ['Why can’t I find an earlier conversation?', 'Temporary conversations are not retained. Confirm that you saved the draft, signed in to the same account, and opened History from the same activity.'],
        ['Why is a conversation not in Library?', 'A saved conversation draft is different from a completed story. Finish generating or saving the story before looking for it in Library.'],
        ['Does my Buddy avatar sync across devices?', 'Yes. A custom Buddy avatar is saved privately to your account and appears when you sign in on another device.'],
      ]],
      ['Account & privacy', [
        ['How do I manage or cancel a subscription?', 'Open Purchase Plan, then Manage Subscription. App Store subscriptions must be managed through Apple.'],
        ['What should I do before uploading or sharing?', 'Use only media you have permission to use. Remove unnecessary identifying information and have a responsible adult review generated content before use or sharing.'],
      ]],
    ],
  },
  French: {
    title: 'Assistance et FAQ', subtitle: 'Des réponses rapides lorsqu’une fonction ne marche pas comme prévu.',
    guide: 'Ouvrir le guide d’utilisation', contact: 'Contacter l’assistance', note: 'Pour apprendre à utiliser chaque fonction pas à pas, consultez le guide d’utilisation.',
    groups: [
      ['Résoudre un problème', [
        ['Pourquoi un bouton est-il désactivé ?', 'Lisez le message près du bouton. Un champ, une photo, la vérification du forfait ou une génération en cours peut bloquer l’étape suivante.'],
        ['Pourquoi la génération a-t-elle échoué ?', 'Attendez un instant puis réessayez une fois. Vérifiez la connexion et le fichier envoyé. Si le problème persiste, notez l’activité et l’heure, puis contactez l’assistance.'],
        ['Pourquoi n’y a-t-il pas de son ?', 'Activez la voix du compagnon, augmentez le volume et autorisez le son dans le navigateur. Certains navigateurs exigent d’abord un clic sur la page.'],
      ]],
      ['Enregistrement et appareils', [
        ['Pourquoi une ancienne conversation est-elle introuvable ?', 'Les conversations temporaires ne sont pas conservées. Vérifiez que le brouillon a été enregistré, que le même compte est utilisé et que l’historique de la bonne activité est ouvert.'],
        ['Pourquoi une conversation n’apparaît-elle pas dans Histoires ?', 'Un brouillon de conversation diffère d’une histoire terminée. Terminez la génération ou l’enregistrement avant de chercher l’histoire.'],
        ['L’avatar du compagnon est-il synchronisé entre les appareils ?', 'Oui. Il est enregistré de manière privée dans votre compte et apparaît lorsque vous vous connectez sur un autre appareil.'],
      ]],
      ['Compte et confidentialité', [
        ['Comment gérer ou annuler un abonnement ?', 'Ouvrez Voir les forfaits, puis Gérer l’abonnement. Les abonnements App Store doivent être gérés chez Apple.'],
        ['Que faut-il vérifier avant un envoi ou un partage ?', 'Utilisez uniquement des médias autorisés, retirez les renseignements personnels inutiles et faites vérifier le contenu par un adulte responsable.'],
      ]],
    ],
  },
  'Simplified Chinese': {
    title: '帮助与常见问题', subtitle: '当功能没有按预期工作时，可以在这里快速找到答案。',
    guide: '打开用户手册', contact: '联系支持', note: '如需查看各项功能的完整操作步骤，请阅读用户手册。',
    groups: [
      ['解决使用问题', [
        ['为什么按钮无法点击？', '查看按钮附近的提示。可能尚未填写必填内容、上传照片、完成方案检查，或当前仍在生成。'],
        ['为什么生成失败？', '稍等后重试一次，并检查网络和上传文件。如果仍然失败，记下功能名称和发生时间后联系支持。'],
        ['为什么没有声音？', '打开活动中的伙伴语音，检查设备音量和浏览器音频权限。部分浏览器需要先点击页面。'],
      ]],
      ['保存与设备', [
        ['为什么找不到之前的对话？', '临时对话不会保留。请确认已保存草稿、使用同一账户，并从同一活动打开历史记录。'],
        ['为什么对话没有出现在故事库？', '对话草稿与已完成故事不同。请先完成故事生成或保存。'],
        ['伙伴头像会在设备间同步吗？', '会。自定义伙伴头像会私密保存到您的账户，并在您登录其他设备时显示。'],
      ]],
      ['账户与隐私', [
        ['如何管理或取消订阅？', '打开购买计划，再选择管理订阅。通过 App Store 购买的订阅需要在 Apple 中管理。'],
        ['上传或分享前应该检查什么？', '只使用获得许可的媒体，删除不必要的身份信息，并由负责成人在使用或分享前审核内容。'],
      ]],
    ],
  },
  'Traditional Chinese': {
    title: '說明與常見問題', subtitle: '當功能沒有按預期運作時，可以在這裡快速找到答案。',
    guide: '開啟使用者手冊', contact: '聯絡支援', note: '如需查看各項功能的完整操作步驟，請閱讀使用者手冊。',
    groups: [
      ['解決使用問題', [
        ['為什麼按鈕無法點選？', '查看按鈕附近的提示。可能尚未填寫必填內容、上傳照片、完成方案檢查，或目前仍在生成。'],
        ['為什麼生成失敗？', '稍後重試一次，並檢查網路和上傳檔案。如果仍然失敗，記下功能名稱和發生時間後聯絡支援。'],
        ['為什麼沒有聲音？', '開啟活動中的夥伴語音，檢查裝置音量和瀏覽器音訊權限。部分瀏覽器需要先點選頁面。'],
      ]],
      ['儲存與裝置', [
        ['為什麼找不到之前的對話？', '臨時對話不會保留。請確認已儲存草稿、使用同一帳戶，並從同一活動開啟歷史記錄。'],
        ['為什麼對話沒有出現在故事庫？', '對話草稿與已完成故事不同。請先完成故事生成或儲存。'],
        ['夥伴頭像會在裝置間同步嗎？', '會。自訂夥伴頭像會以私人方式儲存到您的帳戶，並在您登入其他裝置時顯示。'],
      ]],
      ['帳戶與隱私', [
        ['如何管理或取消訂閱？', '開啟購買計劃，再選擇管理訂閱。透過 App Store 購買的訂閱需要在 Apple 中管理。'],
        ['上傳或分享前應該檢查什麼？', '只使用獲得許可的媒體，刪除不必要的身分資訊，並由負責成人在使用或分享前審核內容。'],
      ]],
    ],
  },
};

export default function FAQPage({ language = 'English' }) {
  const copy = CONTENT[language] || CONTENT.English;
  return <Box sx={{ width: '100%', maxWidth: 920, mx: 'auto', py: 4 }}>
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
        <HelpOutlineRoundedIcon sx={{ color: '#3A6752', fontSize: 34 }} />
        <Typography variant="h4" sx={{ fontWeight: 900, color: '#274D3B' }}>{copy.title}</Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ maxWidth: 680 }}>{copy.subtitle}</Typography>
    </Box>
    <Alert severity="info" sx={{ mb: 3 }} action={<Button href="/user-guide" color="inherit" startIcon={<MenuBookRoundedIcon />}>{copy.guide}</Button>}>
      {copy.note}
    </Alert>
    <Stack spacing={3}>
      {copy.groups.map(([group, items]) => <Box key={group}>
        <Chip label={group} sx={{ mb: 1.2, bgcolor: '#EDF1DF', color: '#274D3B', fontWeight: 800 }} />
        {items.map(([question, answer]) => <Accordion key={question} disableGutters elevation={0} sx={{ border: '1px solid #D6DED2', '&:not(:last-child)': { borderBottom: 0 }, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography sx={{ fontWeight: 800 }}>{question}</Typography></AccordionSummary>
          <AccordionDetails><Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>{answer}</Typography></AccordionDetails>
        </Accordion>)}
      </Box>)}
    </Stack>
    <Button href="mailto:support@marstrix.com" startIcon={<SupportAgentRoundedIcon />} sx={{ mt: 3 }}>{copy.contact}</Button>
  </Box>;
}
